/**
 * Numbered story-event badges on the map (#1, #2 …), matching the Story HUD
 * timeline. Dwells sit on the dwell location; travel legs sit at the midpoint
 * between their start and end. Colours follow the HUD: dwell = accent,
 * travel = secondary. Rendered inside the react-map-gl <Map>.
 *
 * Badges whose screen positions collide (a camera on the same corner as a
 * dwell, two cameras at one intersection) are stacked into a single pill that
 * reads "10–11" / "14·15" (see services/badgeStacking). Zooming in splits them
 * again. The timeline keeps the individual numbers; nothing is renumbered.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import { routeSequenceEntryColor } from '@signals/app-shell/services/routeSequence';
import { useKeepLayersOnTop } from '@signals/app-shell/components/map/useKeepLayersOnTop';
import {
  STACKED_BADGE_CIRCLE_PAINT,
  STACKED_BADGE_TEXT_LAYOUT,
  stackBadges,
  type StackableBadge,
} from '@signals/app-shell/services/badgeStacking';
import { useNarrativeStore, useVisibleStoryEvents, type StoryEvent } from '../../stores/narrativeStore';
import { useHudStyle } from './PathfinderHudKit';

/** Minimal track point shape needed to anchor travel badges on the route. */
export interface StoryLabelTrackPoint {
  studentId: string;
  deviceId?: string;
  timestamp: Date;
  coordinates: [number, number]; // [lat, lng]
}

const CIRCLE_LAYER = 'story-event-badge-circles';
const TEXT_LAYER = 'story-event-badge-text';
const BADGE_LAYERS = [CIRCLE_LAYER, TEXT_LAYER] as const;
/** Draw order when badges coincide: dwell on top, then camera, then travel. */
const KIND_RANK: Record<string, number> = { dwell: 2, camera: 1, travel: 0 };
const kindRank = (k: string) => KIND_RANK[k] ?? 0;
const kindRadius = (k: string) => (k === 'dwell' ? 1 : 0.82);
const ANCHOR_KINDS: ReadonlySet<string> = new Set(['dwell']);

/** Whether story badges are currently drawn (for anchors that should not cover them). */
export const useStoryBadgesShowing = () =>
  useNarrativeStore((s) => s.panelsOpen.story && s.storyMapLabels && s.storyEvents.length > 0);

const StoryEventMapLabels: React.FC<{ dataPoints?: StoryLabelTrackPoint[] }> = ({ dataPoints }) => {
  // Same cropped list the Story HUD shows (numbers are stable across cropping).
  const events = useVisibleStoryEvents();
  const storyOpen = useNarrativeStore((s) => s.panelsOpen.story);
  const enabled = useNarrativeStore((s) => s.storyMapLabels);
  const { accent, secondary, tokens } = useHudStyle();

  // Track points per entity, time-sorted, so a travel leg's badge can sit on
  // the route at the leg's mid-time rather than the straight-line midpoint
  // (which lands off-route whenever the path bends).
  const sortedByEntity = useMemo(() => {
    const m = new Map<string, StoryLabelTrackPoint[]>();
    (dataPoints ?? []).forEach((p) => {
      if (!p.coordinates || !(p.timestamp instanceof Date)) return;
      // Story Mode keys travel legs by `studentId || deviceId`.
      const key = p.studentId || p.deviceId || '';
      const arr = m.get(key);
      if (arr) arr.push(p);
      else m.set(key, [p]);
    });
    m.forEach((arr) => arr.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()));
    return m;
  }, [dataPoints]);

  const raw = useMemo<StackableBadge[]>(() => {
    const out: StackableBadge[] = [];
    const onRouteAt = (entityId: string, start: Date, end: Date): [number, number] | undefined => {
      const pts = sortedByEntity.get(entityId);
      if (!pts || pts.length === 0) return undefined;
      const t0 = start.getTime();
      const t1 = end.getTime();
      // Binary-search the first point at/after the leg start, then walk to the
      // point nearest the leg's mid-time within the window.
      let lo = 0;
      let hi = pts.length - 1;
      while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (pts[mid].timestamp.getTime() < t0) lo = mid + 1;
        else hi = mid;
      }
      const target = (t0 + t1) / 2;
      let best: StoryLabelTrackPoint | undefined;
      for (let i = lo; i < pts.length; i++) {
        const t = pts[i].timestamp.getTime();
        if (t > t1) break;
        if (!best || Math.abs(t - target) < Math.abs(best.timestamp.getTime() - target)) best = pts[i];
      }
      return best?.coordinates;
    };

    events.forEach((e: StoryEvent, i) => {
      // Story events carry [lat, lng]; GeoJSON wants [lng, lat].
      let ll: [number, number] | undefined;
      if ((e.type === 'dwell' || e.type === 'camera') && e.location) ll = e.location;
      else if (e.type === 'travel') {
        ll = onRouteAt(e.entityId, e.startTime, e.endTime);
        // Inferred legs have no track; fall back to the straight-line midpoint.
        if (!ll && e.startLocation && e.endLocation) {
          ll = [(e.startLocation[0] + e.endLocation[0]) / 2, (e.startLocation[1] + e.endLocation[1]) / 2];
        }
      }
      if (!ll || !Number.isFinite(ll[0]) || !Number.isFinite(ll[1])) return;
      out.push({
        lngLat: [ll[1], ll[0]],
        // Stable number from the full sequence (not the index of the cropped list).
        seq: e.seq ?? i + 1,
        kind: e.type,
        color:
          e.type === 'dwell'
            ? accent
            : e.type === 'camera'
              ? routeSequenceEntryColor({ kind: 'camera', category: e.category ?? undefined })
              : secondary,
      });
    });
    return out;
  }, [events, accent, secondary, sortedByEntity]);

  const showing = storyOpen && enabled && raw.length > 0;

  // Re-stack after each pan/zoom settles (stacking is in screen space).
  const { current: map } = useMap();
  const [viewTick, setViewTick] = useState(0);
  useEffect(() => {
    const m = map?.getMap();
    if (!m || !showing) return;
    const bump = () => setViewTick((t) => t + 1);
    m.on('moveend', bump);
    m.on('zoomend', bump);
    return () => {
      m.off('moveend', bump);
      m.off('zoomend', bump);
    };
  }, [map, showing]);

  const data = useMemo(() => {
    void viewTick; // stacking depends on the current projection
    const m = map?.getMap();
    return stackBadges(raw, (ll) => (m ? m.project(ll) : null), {
      rank: kindRank,
      radius: kindRadius,
      anchorKinds: ANCHOR_KINDS,
    });
  }, [raw, map, viewTick]);

  // Keep the badges above everything else (dwell circles, cameras, …).
  const getMap = useCallback(() => map?.getMap(), [map]);
  useKeepLayersOnTop(getMap, BADGE_LAYERS, showing);

  if (!showing) return null;

  return (
    <Source id="story-event-badges" type="geojson" data={data}>
      <Layer
        id={CIRCLE_LAYER}
        type="circle"
        layout={{ 'circle-sort-key': ['get', 'sort'] }}
        paint={STACKED_BADGE_CIRCLE_PAINT as any}
      />
      <Layer
        id={TEXT_LAYER}
        type="symbol"
        layout={STACKED_BADGE_TEXT_LAYOUT as any}
        paint={{
          'text-color': ['case', ['==', ['get', 'kind'], 'dwell'], tokens.onAccent, ['==', ['get', 'kind'], 'camera'], '#ffffff', '#111111'],
        }}
      />
    </Source>
  );
};

export default StoryEventMapLabels;
